import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
  Grid,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { Location, LocationType } from '../../types';

interface LocationFormData {
  name: string;
  type: LocationType;
  code?: string;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  currency?: string;
  organizationId: string;
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  isActive: boolean;
}

export const LocationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<LocationFormData>();

  const watchType = watch('type');
  const canSellToCustomers = watch('canSellToCustomers');
  const canFulfillOnlineOrders = watch('canFulfillOnlineOrders');
  const canReceiveTransfers = watch('canReceiveTransfers');
  const canSendTransfers = watch('canSendTransfers');
  const isActive = watch('isActive');

  const { data, isLoading, error } = useQuery<Location>({
    queryKey: ['location', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/locations/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/organizations');
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        type: data.type,
        code: data.code || '',
        description: data.description || '',
        address: data.address || '',
        city: data.city || '',
        wilaya: data.wilaya || '',
        zipCode: data.zipCode || '',
        country: data.country,
        phone: data.phone || '',
        email: data.email || '',
        currency: data.currency || 'DZD',
        organizationId: data.organizationId || '',
        canSellToCustomers: data.canSellToCustomers || false,
        canFulfillOnlineOrders: data.canFulfillOnlineOrders || false,
        canReceiveTransfers: data.canReceiveTransfers || false,
        canSendTransfers: data.canSendTransfers || false,
        isActive: data.isActive || false,
      });
    }
  }, [data, reset]);

  const updateMutation = useMutation({
    mutationFn: (formData: LocationFormData) => axiosInstance.patch(`/locations/${id}`, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      showMessage('Location updated successfully', 'success');
      navigate('/locations');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update location', 'error');
    },
  });

  const onSubmit = (formData: LocationFormData) => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load location'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Location
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Location name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Location Name"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  {...register('type', { required: 'Type is required' })}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  fullWidth
                  select
                  label="Type"
                  value={watchType || ''}
                  onChange={(e) => setValue('type', e.target.value as LocationType)}
                >
                  <MenuItem value="store">Store</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  {...register('code')}
                  fullWidth
                  label="Code"
                  placeholder="e.g., STORE-001"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('organizationId', { required: 'Organization is required' })}
                  error={!!errors.organizationId}
                  helperText={errors.organizationId?.message}
                  fullWidth
                  select
                  label="Organization"
                >
                  <MenuItem value="">Select Organization</MenuItem>
                  {organizations?.map((org: any) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('address')}
                  fullWidth
                  multiline
                  rows={2}
                  label="Address"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('city')}
                  fullWidth
                  label="City"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('wilaya')}
                  fullWidth
                  label="Wilaya"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('zipCode')}
                  fullWidth
                  label="Zip Code"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('country')}
                  fullWidth
                  label="Country"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('currency')}
                  fullWidth
                  select
                  label="Currency"
                >
                  <MenuItem value="DZD">DZD - Algerian Dinar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                </TextField>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('phone')}
                  fullWidth
                  label="Phone"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('email')}
                  fullWidth
                  type="email"
                  label="Email"
                />
              </Grid>

              {/* Capabilities */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Capabilities
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canSellToCustomers || false}
                      onChange={(e) => setValue('canSellToCustomers', e.target.checked)}
                    />
                  }
                  label="Can sell to customers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canFulfillOnlineOrders || false}
                      onChange={(e) => setValue('canFulfillOnlineOrders', e.target.checked)}
                    />
                  }
                  label="Can fulfill online orders"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canReceiveTransfers || false}
                      onChange={(e) => setValue('canReceiveTransfers', e.target.checked)}
                    />
                  }
                  label="Can receive transfers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canSendTransfers || false}
                      onChange={(e) => setValue('canSendTransfers', e.target.checked)}
                    />
                  }
                  label="Can send transfers"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive || false}
                      onChange={(e) => setValue('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => navigate('/locations')}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
